import React, { useEffect, useState } from 'react';
import {
  Card,
  Button,
  Steps,
  Typography,
  Alert,
  Upload,
  Space,
  Select,
  message,
  Table,
  Divider,
  Modal,
} from 'antd';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';
import { DeleteOutlined, DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import { getCustomerList } from '../../services/project';
import request from '../../utils/request';

const { Paragraph, Text } = Typography;
const { Dragger } = Upload;

type BatchImportError = { row: number; message: string };

type BatchImportResponse = {
  template_version?: string;
  ok: boolean;
  detail?: string | null;
  errors: BatchImportError[];
  created?: number;
  failed?: number;
  valid_rows?: number;
  total_data_rows?: number;
  dry_run?: boolean;
  results?: unknown[];
};

/** 产品图片支持的文件：压缩包（ZIP/RAR）或直接上传图片 */
const IMAGE_EXTS = ['.png', '.jpg', '.jpeg', '.gif', '.webp'];
const IMAGE_UPLOAD_EXTS = ['.zip', '.rar', ...IMAGE_EXTS];
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
/** 一次最多上传的图片/压缩包文件数（服务器单次请求最多接收 100 个文件） */
const MAX_IMAGE_UPLOAD_FILES = 50;

const fileExt = (name: string) => {
  const n = String(name || '').trim().toLowerCase();
  const i = n.lastIndexOf('.');
  return i >= 0 ? n.slice(i) : '';
};

const formatFileSize = (size?: number) => {
  if (!size) return '';
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * 批量创建产品：下载动态模板、上传 Excel 与可选产品图片（ZIP/RAR 压缩包或图片，可多个），调用 /api/v1/products/batch-import/
 */
const BatchCreateProduct: React.FC = () => {
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [excelFile, setExcelFile] = useState<UploadFile | null>(null);
  const [imageFiles, setImageFiles] = useState<UploadFile[]>([]);
  const [customerId, setCustomerId] = useState<number | undefined>(undefined);
  const [customerOptions, setCustomerOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [customerOptionsLoading, setCustomerOptionsLoading] = useState(false);
  /** 仅在一次完整成功导入后展示卡片中的创建条数 */
  const [importSuccessCount, setImportSuccessCount] = useState<number | null>(null);
  const [failureModal, setFailureModal] = useState<{
    open: boolean;
    title: string;
    data: BatchImportResponse | null;
  }>({ open: false, title: '', data: null });

  const needCustomerField = user?.role === 'admin' || user?.role === 'staff';

  useEffect(() => {
    if (!needCustomerField) return;
    let cancelled = false;
    (async () => {
      setCustomerOptionsLoading(true);
      try {
        const list = await getCustomerList({ onlyMain: true });
        if (cancelled) return;
        const opts = (list || []).map((c: { id: string; username?: string; name?: string; customer_code?: string }) => {
          const id = Number(c.id);
          const code = c.customer_code || '';
          const name = c.name || c.username || '';
          const label = code ? `${name}（${code}）` : name || `用户 ${id}`;
          return { value: id, label };
        });
        setCustomerOptions(opts);
      } catch {
        if (!cancelled) setCustomerOptions([]);
      } finally {
        if (!cancelled) setCustomerOptionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [needCustomerField]);

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const res = await request.get('/api/v1/products/batch-import/template/', {
        responseType: 'arraybuffer',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'product_batch_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      message.success('模板已下载');
    } catch (e: unknown) {
      console.error(e);
      message.error('模板下载失败');
    } finally {
      setDownloading(false);
    }
  };

  const buildFormData = (dryRun: boolean) => {
    const excel = excelFile?.originFileObj;
    const fd = new FormData();
    if (excel) fd.append('excel', excel);
    imageFiles.forEach((f) => {
      if (f.originFileObj) fd.append('images_zip', f.originFileObj);
    });
    if (needCustomerField && customerId != null) {
      fd.append('customer', String(customerId));
    }
    if (dryRun) fd.append('dry_run', 'true');
    return fd;
  };

  /** 每次点击：先校验；仅当校验通过且存在数据行时再正式导入，否则只展示校验结果并阻断导入 */
  const handleSubmit = async () => {
    const excel = excelFile?.originFileObj;
    if (!excel) {
      message.warning('请先选择 Excel 文件');
      return;
    }
    if (needCustomerField && customerId == null) {
      message.warning('请选择客户账号');
      return;
    }

    setSubmitting(true);
    setImportSuccessCount(null);
    try {
      const { data: checkData } = await request.post<BatchImportResponse>(
        '/api/v1/products/batch-import/',
        buildFormData(true),
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      if (!checkData.ok || (checkData.errors?.length ?? 0) > 0) {
        setFailureModal({ open: true, title: '校验未通过', data: checkData });
        return;
      }

      if (checkData.detail === '无数据行') {
        setFailureModal({ open: true, title: '校验未通过', data: checkData });
        return;
      }

      message.success('校验通过，正在导入…');
      const { data: importData } = await request.post<BatchImportResponse>(
        '/api/v1/products/batch-import/',
        buildFormData(false),
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      if (importData.ok) {
        setImportSuccessCount(importData.created ?? 0);
        setExcelFile(null);
        setImageFiles([]);
      } else {
        setFailureModal({ open: true, title: '导入未完成', data: importData });
      }
    } catch (e: unknown) {
      console.error(e);
      message.error('提交失败，请检查网络或登录状态');
    } finally {
      setSubmitting(false);
    }
  };

  /** 一次选择/拖入的多个文件只在第一个文件时统一处理：过滤格式、超大图片、重复文件和超出数量的文件 */
  const handleImageFilesSelected = (file: RcFile, batch: RcFile[]) => {
    if (batch.length > 0 && file.uid !== batch[0].uid) return false;
    const unsupported: string[] = [];
    const tooLarge: string[] = [];
    const next = [...imageFiles];
    let overLimit = 0;
    for (const f of batch.length > 0 ? batch : [file]) {
      const ext = fileExt(f.name);
      if (!IMAGE_UPLOAD_EXTS.includes(ext)) {
        unsupported.push(f.name);
        continue;
      }
      if (IMAGE_EXTS.includes(ext) && f.size > MAX_IMAGE_BYTES) {
        tooLarge.push(f.name);
        continue;
      }
      const duplicate = next.some(
        (x) => x.name === f.name && x.size === f.size && x.originFileObj?.lastModified === f.lastModified,
      );
      if (duplicate) continue;
      if (next.length >= MAX_IMAGE_UPLOAD_FILES) {
        overLimit += 1;
        continue;
      }
      next.push({ uid: f.uid, name: f.name, size: f.size, originFileObj: f });
    }
    if (unsupported.length > 0) {
      message.warning(`不支持的文件格式：${unsupported.join('、')}（支持 zip、rar、png、jpg、jpeg、gif、webp）`);
    }
    if (tooLarge.length > 0) {
      message.warning(`图片超过 2MB：${tooLarge.join('、')}`);
    }
    if (overLimit > 0) {
      message.warning(`一次最多上传 ${MAX_IMAGE_UPLOAD_FILES} 个文件，已忽略 ${overLimit} 个；图片较多时请打包成 ZIP 或 RAR 上传`);
    }
    setImageFiles(next);
    return false;
  };

  const errorColumns = [
    { title: 'Excel 行号', dataIndex: 'row', key: 'row', width: 100 },
    { title: '原因', dataIndex: 'message', key: 'message' },
  ];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-medium m-0 mb-6">批量创建产品</h1>

      <Card className="mb-6" title="操作步骤" size="small">
        <Steps
          className="min-w-[200px]"
          size="small"
          current={-1}
          items={[
            { title: '下载模板' },
            { title: '填写并上传Excel,可选上传产品图片或压缩文件' },
            { title: '校验并导入,若校验失败请修改后再次尝试导入' },
          ]}
        />
      </Card>

      <Card title="操作">
        <Space direction="vertical" size="large" className="w-full">
          <div>
            <Button
              type="primary"
              ghost
              icon={<DownloadOutlined />}
              loading={downloading}
              onClick={handleDownloadTemplate}
            >
              下载模板
            </Button>
          </div>

          <div>
            <Text strong className="block mb-2">
              产品信息列表(必填, 格式:xlsx)
            </Text>
            <Upload
              maxCount={1}
              showUploadList={false}
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              fileList={excelFile ? [excelFile] : []}
              beforeUpload={(file) => {
                setExcelFile({ uid: file.uid, name: file.name, originFileObj: file });
                return false;
              }}
              onRemove={() => setExcelFile(null)}
            >
              <Button type="primary" style={{ marginRight: '10pt' }}>
                选择 Excel
              </Button>
            </Upload>
            {excelFile ? (
              <div className="mt-4 inline-flex max-w-full flex-wrap items-center gap-2">
                <Text type="secondary" className="mb-0 shrink-0">
                  已上传:
                </Text>
                <Text className="mb-0 min-w-0" ellipsis={{ tooltip: excelFile.name }} style={{ maxWidth: 420 }}>
                  {excelFile.name}
                </Text>
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  aria-label="删除"
                  className="shrink-0"
                  onClick={() => setExcelFile(null)}
                />
              </div>
            ) : null}
          </div>

          <div>
            <Text strong className="block mb-2">
              产品图片(可选,格式:zip/rar/png/jpg/jpeg/gif/webp,可多选)
            </Text>
            <Dragger
              multiple
              showUploadList={false}
              accept={IMAGE_UPLOAD_EXTS.join(',')}
              fileList={imageFiles}
              beforeUpload={handleImageFilesSelected}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽图片或压缩包（可多选）</p>
              <p className="ant-upload-hint">
                可直接上传图片（png/jpg/jpeg/gif/webp），也可上传 ZIP/RAR 压缩包；一次最多 {MAX_IMAGE_UPLOAD_FILES} 个文件
              </p>
              <p className="ant-upload-hint">
                图片文件名（含压缩包内的图片）应与表格中信息一致；单张不超过 2MB
              </p>
            </Dragger>
            {imageFiles.length > 0 ? (
              <div className="mt-4 product-image-upload-list">
                <div className="flex items-center gap-2">
                  <Text type="secondary" className="mb-0">
                    已上传 {imageFiles.length} 个文件:
                  </Text>
                  <Button type="link" danger size="small" onClick={() => setImageFiles([])}>
                    全部删除
                  </Button>
                </div>
                {imageFiles.map((f) => (
                  <div key={f.uid} className="flex max-w-full items-center gap-2">
                    <Text className="mb-0 min-w-0" ellipsis={{ tooltip: f.name }} style={{ maxWidth: 420 }}>
                      {f.name}
                    </Text>
                    <Text type="secondary" className="mb-0 shrink-0 text-xs">
                      {formatFileSize(f.size)}
                    </Text>
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      aria-label="删除"
                      className="shrink-0"
                      onClick={() => setImageFiles((prev) => prev.filter((x) => x.uid !== f.uid))}
                    />
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {needCustomerField ? (
            <div>
              <Text strong className="block mb-2">
                选择客户账号
              </Text>
              <Select
                showSearch
                allowClear
                placeholder="请选择客户主账号"
                loading={customerOptionsLoading}
                optionFilterProp="label"
                value={customerId}
                onChange={(v) => setCustomerId(v === undefined ? undefined : Number(v))}
                options={customerOptions}
                style={{ maxWidth: 480, width: '100%' }}
              />
              <Paragraph type="secondary" className="mt-1 mb-0 text-sm">
                所有批量创建的产品记录将归属于该客户。
              </Paragraph>
            </div>
          ) : null}

          <Divider className="mt-8 mb-4" />
          <Button type="primary" loading={submitting} onClick={handleSubmit}>
            校验并导入
          </Button>
        </Space>
      </Card>

      {importSuccessCount !== null ? (
        <Card title="执行结果" className="mt-6">
          <Text>已成功创建 {importSuccessCount} 条产品记录</Text>
        </Card>
      ) : null}

      <Modal
        title={failureModal.title}
        open={failureModal.open}
        onOk={() => setFailureModal({ open: false, title: '', data: null })}
        footer={[
          <Button key="ok" type="primary" onClick={() => setFailureModal({ open: false, title: '', data: null })}>
            返回修改
          </Button>,
        ]}
        closable={false}
        maskClosable={false}
        keyboard={false}
        width={720}
        destroyOnClose
      >
        <Space direction="vertical" className="w-full" size="middle">
          {failureModal.data?.detail ? (
            <Alert type="warning" message={failureModal.data.detail} showIcon />
          ) : null}
          {failureModal.data?.errors && failureModal.data.errors.length > 0 ? (
            <Table
              size="small"
              pagination={false}
              rowKey={(r) => `${r.row}-${r.message}`}
              columns={errorColumns}
              dataSource={failureModal.data.errors}
            />
          ) : null}
          {!failureModal.data?.detail &&
          (!failureModal.data?.errors || failureModal.data.errors.length === 0) &&
          failureModal.data ? (
            <Text type="secondary">请修改 Excel 或产品图片后重新提交。</Text>
          ) : null}
        </Space>
      </Modal>
    </div>
  );
};

export default BatchCreateProduct;
